import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { cards as cardsApi, templates as templatesApi, type Card, type Activity, type AgentQuestion, type Board, type CardAttachment, type TeamTemplate, type PhaseActionName } from '../lib/api';
import { cn, STATUS_COLORS, PHASE_COLORS, timeAgo } from '../lib/utils';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useToast } from './Toast';
import { MarkdownEditor } from './MarkdownEditor';
import { MarkdownView } from './MarkdownView';
import { computeDropStates, type MinimalWorkflowPhase } from '../lib/move-rules';

const ACTION_LABELS: Record<PhaseActionName, string> = {
  createPR: 'Create Pull Request',
  autoMergePR: 'Auto-merge PR',
  trackDeployment: 'Track Deployment',
};

const ACTIVITY_DOT_COLORS: Record<string, string> = {
  analysis: 'bg-blue-400',
  planning: 'bg-blue-400',
  coding: 'bg-purple-400',
  testing: 'bg-green-400',
  review: 'bg-cyan-400',
  deployment: 'bg-orange-400',
  feedback: 'bg-amber-400',
  error: 'bg-red-400',
  'status-update': 'bg-primary-500',
  question: 'bg-amber-400',
  migration: 'bg-orange-400',
  escalation: 'bg-red-500',
};

interface Props {
  card: Card;
  boardId: string;
  onClose: () => void;
  board?: Board;
  workflow?: MinimalWorkflowPhase[];
  onMoveCard?: (cardId: string, columnId: string) => void;
  isLoadingFull?: boolean;
  /** Full list of cards on the board — used to render the "Blocked by" picker */
  allCards?: Card[];
}

export function CardDetail({ card, boardId, onClose, board, workflow, onMoveCard, isLoadingFull, allCards }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description);
  const [tags, setTags] = useState<string[]>(card.metadata.tags);
  const [plan, setPlan] = useState(card.metadata.plan ?? '');
  const [blockedBy, setBlockedBy] = useState<string[]>(card.metadata.blockedBy ?? []);
  const [blockedByOriginal, setBlockedByOriginal] = useState<string[]>(card.metadata.blockedBy ?? []);
  const [showBlockedByPicker, setShowBlockedByPicker] = useState(false);
  const [answerInputs, setAnswerInputs] = useState<Record<string, string>>({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [viewingOutput, setViewingOutput] = useState<Activity | null>(null);
  const [viewingOutputContent, setViewingOutputContent] = useState<string | null>(null);
  const [viewingOutputFetching, setViewingOutputFetching] = useState(false);
  const [planLoaded, setPlanLoaded] = useState(!!card.metadata.plan);
  const [planOriginal, setPlanOriginal] = useState(card.metadata.plan ?? '');
  const deleteTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>(card.attachmentUrls ?? {});
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachFileInputRef = useRef<HTMLInputElement>(null);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptAgentRoleId, setPromptAgentRoleId] = useState('');

  useEscapeKey(onClose);

  // Auto-reset delete confirmation after 3 seconds
  useEffect(() => {
    if (deleteConfirm) {
      deleteTimerRef.current = setTimeout(() => setDeleteConfirm(false), 3000);
      return () => clearTimeout(deleteTimerRef.current);
    }
  }, [deleteConfirm]);

  useEffect(() => {
    setTitle(card.title);
    setDescription(card.description);
    setTags(card.metadata.tags);
    // Only update plan when the card has an explicit value — summary responses from the
    // list endpoint strip metadata.plan (undefined), which would otherwise wipe a
    // previously-loaded full plan out of local state.
    if (card.metadata.plan !== undefined) {
      setPlan(card.metadata.plan);
      setPlanOriginal(card.metadata.plan);
      setPlanLoaded(!!card.metadata.plan);
    }
    if (card.attachmentUrls) {
      setAttachmentUrls(card.attachmentUrls);
    }
    const nextBlockedBy = card.metadata.blockedBy ?? [];
    setBlockedBy(nextBlockedBy);
    setBlockedByOriginal(nextBlockedBy);
  }, [card]);

  // Fetch plan from S3 if planUrl is available
  useEffect(() => {
    if (card.planUrl && !plan) {
      fetch(card.planUrl)
        .then(res => res.ok ? res.text() : null)
        .then(text => {
          if (text) {
            setPlan(text);
            setPlanOriginal(text);
            setPlanLoaded(true);
          }
        })
        .catch(() => { /* fall back to inline plan */ });
    }
  }, [card.planUrl]);

  // Activity feed — per-card query
  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ['card-activities', boardId, card.id],
    queryFn: () => cardsApi.activities(boardId, card.id),
    refetchInterval: 30_000,
  });
  const activities: Activity[] = activitiesData?.data || [];

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Card> & { tags?: string[] }) =>
      cardsApi.update(boardId, card.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      toast('Card updated', 'success');
    },
    onError: () => toast('Failed to update card', 'error'),
  });

  // Answer question mutation
  const answerMutation = useMutation({
    mutationFn: ({ questionId, answer }: { questionId: string; answer: string }) =>
      cardsApi.answerQuestion(boardId, card.id, questionId, answer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      toast('Answer submitted', 'success');
    },
    onError: () => toast('Failed to submit answer', 'error'),
  });

  // Template query (lazy — only fires when prompt input is expanded)
  const { data: templateData } = useQuery({
    queryKey: ['template', board?.settings.defaultTemplate],
    queryFn: () => templatesApi.get(board!.settings.defaultTemplate!),
    enabled: promptExpanded && !!board?.settings.defaultTemplate,
    staleTime: 5 * 60 * 1000,
  });
  const templateAgents = (templateData as any)?.data?.agents ?? (templateData as any)?.agents ?? [];

  // Set default agent when template loads
  useEffect(() => {
    if (templateAgents.length > 0 && !promptAgentRoleId) {
      const builder = templateAgents.find((a: any) => a.type === 'builder');
      setPromptAgentRoleId(builder?.id ?? templateAgents[0].id);
    }
  }, [templateAgents, promptAgentRoleId]);

  // Submit prompt mutation
  const promptMutation = useMutation({
    mutationFn: (data: { prompt: string; agentRoleId: string }) =>
      cardsApi.submitPrompt(boardId, card.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      queryClient.invalidateQueries({ queryKey: ['card-activities', boardId, card.id] });
      toast('Prompt submitted', 'success');
      setPromptText('');
      setPromptExpanded(false);
    },
    onError: (err: any) => toast(err?.message || 'Failed to submit prompt', 'error'),
  });

  // Acknowledge reeval mutation
  const reevalMutation = useMutation({
    mutationFn: () => cardsApi.acknowledgeReeval(boardId, card.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      toast('Displacement acknowledged', 'success');
    },
    onError: () => toast('Failed to acknowledge', 'error'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => cardsApi.delete(boardId, card.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      toast('Card deleted', 'success');
      onClose();
    },
    onError: () => toast('Failed to delete card', 'error'),
  });

  // Cancel work mutation
  const cancelWorkMutation = useMutation({
    mutationFn: () => cardsApi.cancelWork(boardId, card.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      toast('Work cancelled', 'success');
    },
    onError: () => toast('Failed to cancel work', 'error'),
  });

  // Retry a phase-action (createPR, autoMergePR, trackDeployment)
  const retryActionMutation = useMutation({
    mutationFn: (action: PhaseActionName) => cardsApi.retryAction(boardId, card.id, action),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
      queryClient.invalidateQueries({ queryKey: ['card-activities', boardId, card.id] });
      if (res.success) {
        toast(res.message || 'Retry succeeded', 'success');
      } else {
        toast(res.error?.message || 'Retry failed', 'error');
      }
    },
    onError: (err: any) => toast(err?.message || 'Failed to retry action', 'error'),
  });

  const isLocked = !!(card.metadata.claim && new Date(card.metadata.claim.expiresAt) > new Date());

  // Compute the next column for "Move to Next Phase" button
  const nextColumn = useMemo(() => {
    if (!board || !onMoveCard) return null;
    const sorted = [...board.columns].sort((a, b) => a.order - b.order);
    const currentIdx = sorted.findIndex((c) => c.id === card.columnId);
    if (currentIdx === -1 || currentIdx >= sorted.length - 1) return null;
    const dropStates = computeDropStates(board.columns, card.columnId, workflow);
    const candidate = sorted[currentIdx + 1];
    if (dropStates.get(candidate.id) === 'valid') return candidate;
    return null;
  }, [board, card.columnId, workflow, onMoveCard]);

  const handleAttachImages = async (files: File[]) => {
    setUploadingAttachment(true);
    try {
      const now = new Date().toISOString();
      const existing = card.metadata.attachments ?? [];
      const slots = Math.max(0, 10 - existing.length);
      const toUpload = files.slice(0, slots);

      const uploadResults = await Promise.allSettled(
        toUpload.map(async (file) => {
          const urlRes = await cardsApi.attachmentUploadUrl(boardId, card.id, {
            filename: file.name,
            contentType: file.type,
            size: file.size,
          });
          if (!urlRes.success || !urlRes.data) throw new Error('Failed to get upload URL');
          const { uploadUrl, s3Key, attachmentId, filename } = urlRes.data;

          await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });

          return {
            id: attachmentId,
            s3Key,
            filename,
            contentType: file.type,
            size: file.size,
            uploadedAt: now,
            uploadedBy: '',
          } satisfies CardAttachment;
        })
      );

      const newAttachments: CardAttachment[] = uploadResults
        .filter((r): r is PromiseFulfilledResult<CardAttachment> => r.status === 'fulfilled')
        .map((r) => r.value);

      if (newAttachments.length > 0) {
        const merged = [...existing, ...newAttachments];
        await cardsApi.update(boardId, card.id, {
          metadata: { attachments: merged } as any,
        });
        queryClient.invalidateQueries({ queryKey: ['card', boardId, card.id] });
        queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
        toast(`${newAttachments.length} image${newAttachments.length > 1 ? 's' : ''} attached`, 'success');
      }
    } catch {
      toast('Failed to attach images', 'error');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleViewOutput = useCallback(async (activity: Activity) => {
    setViewingOutput(activity);
    setViewingOutputContent(null);
    if (activity.contentUrl) {
      setViewingOutputFetching(true);
      try {
        const res = await fetch(activity.contentUrl);
        if (res.ok) setViewingOutputContent(await res.text());
      } catch { /* fall back to description */ }
      setViewingOutputFetching(false);
    }
  }, []);

  const handleAnswer = (questionId: string) => {
    const answer = answerInputs[questionId]?.trim();
    if (!answer) return;
    answerMutation.mutate({ questionId, answer });
    setAnswerInputs((prev) => ({ ...prev, [questionId]: '' }));
  };

  const blockedByChanged =
    blockedBy.length !== blockedByOriginal.length ||
    blockedBy.some((id, i) => id !== blockedByOriginal[i]);

  const hasChanges =
    title !== card.title ||
    description !== card.description ||
    plan !== planOriginal ||
    blockedByChanged;

  const handleSave = () => {
    const changes: Record<string, unknown> = {};
    if (title !== card.title) changes.title = title;
    if (description !== card.description) changes.description = description;
    if (plan !== planOriginal) changes.plan = plan;
    if (blockedByChanged) {
      // blockedBy lives in metadata — send as partial metadata; the server
      // shallow-merges inputMetadata over existingCard.metadata (see
      // packages/api/src/routes/cards.ts PUT handler).
      changes.metadata = { blockedBy };
    }
    if (Object.keys(changes).length > 0) {
      updateMutation.mutate(changes as any);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 md:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-800 bg-surface-900 shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3 md:px-6 md:py-4">
          <div className="flex items-center gap-2">
            {card.status !== 'draft' && (
              <span className={cn('text-xs font-semibold uppercase', STATUS_COLORS[card.status] || 'text-gray-400')}>
                {card.status.replace(/-/g, ' ')}
              </span>
            )}
            {card.metadata.inputRequired && (
              <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-400">
                Input Required
              </span>
            )}
            {card.metadata.needsReeval && (
              <span className="rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-semibold text-orange-400">
                Needs Review
              </span>
            )}
            {isLocked && (
              <span className="flex items-center gap-1.5 rounded-full bg-primary-500/20 px-2 py-0.5 text-[10px] font-semibold text-primary-400" title={`Runner: ${card.metadata.claim!.runnerId}\nPhase: ${card.metadata.claim!.phase}\nClaimed: ${new Date(card.metadata.claim!.claimedAt).toLocaleString()}`}>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary-500" />
                </span>
                Agent Working
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Hidden file input for attaching images */}
            <input
              ref={attachFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
              multiple
              className="sr-only"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) handleAttachImages(files);
                e.target.value = '';
              }}
            />
            {/* Attach images button */}
            <button
              onClick={() => attachFileInputRef.current?.click()}
              disabled={uploadingAttachment || isLocked}
              className="rounded-lg p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300 disabled:opacity-40"
              title="Attach images"
            >
              {uploadingAttachment ? (
                <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
              )}
            </button>
            {nextColumn && !isLocked && onMoveCard && (
              <button
                onClick={() => onMoveCard(card.id, nextColumn.id)}
                className="flex items-center gap-1.5 rounded-lg bg-primary-600/20 px-2.5 py-1 text-xs font-medium text-primary-400 hover:bg-primary-600/30 active:bg-primary-600/40"
                title={`Move to ${nextColumn.name}`}
              >
                <span className="max-w-[100px] truncate">{nextColumn.name}</span>
                <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            )}
            {isLocked && (
              <button
                onClick={() => cancelWorkMutation.mutate()}
                disabled={cancelWorkMutation.isPending}
                className="rounded-lg px-2 py-1 text-xs font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
              >
                {cancelWorkMutation.isPending ? 'Cancelling...' : 'Cancel Work'}
              </button>
            )}
            <motion.button
              onClick={() => {
                if (deleteConfirm) {
                  deleteMutation.mutate();
                  setDeleteConfirm(false);
                } else {
                  setDeleteConfirm(true);
                }
              }}
              disabled={isLocked || deleteMutation.isPending}
              animate={{
                width: deleteConfirm ? 'auto' : 28,
                borderColor: deleteConfirm ? 'rgba(239, 68, 68, 0.5)' : 'rgba(239, 68, 68, 0)',
              }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className={cn(
                'flex items-center justify-center overflow-hidden rounded-lg border disabled:opacity-50',
                deleteConfirm
                  ? 'bg-transparent px-3 py-1 text-red-400'
                  : 'border-transparent p-1 text-gray-500 hover:bg-red-500/10 hover:text-red-400'
              )}
              title="Delete card"
            >
              <AnimatePresence mode="wait">
                {deleteConfirm ? (
                  <motion.span
                    key="confirm"
                    initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                    transition={{ duration: 0.2 }}
                    className="whitespace-nowrap text-xs font-medium"
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Confirm?'}
                  </motion.span>
                ) : (
                  <motion.svg
                    key="icon"
                    initial={{ opacity: 0, rotate: 90, scale: 0.5 }}
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: -90, scale: 0.5 }}
                    transition={{ duration: 0.2 }}
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </motion.svg>
                )}
              </AnimatePresence>
            </motion.button>
            <button onClick={onClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Output viewer */}
        {viewingOutput && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center p-4"
            onClick={() => { setViewingOutput(null); setViewingOutputContent(null); }}
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-2xl" />
            <div
              className="relative z-10 flex max-h-[85%] w-full flex-col overflow-hidden rounded-xl border border-gray-700 bg-surface-900 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between border-b border-gray-800 px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={cn('h-2 w-2 shrink-0 rounded-full', ACTIVITY_DOT_COLORS[viewingOutput.type] || 'bg-gray-500')} />
                  <span className="truncate text-sm font-medium text-gray-200">{viewingOutput.title}</span>
                  <span className={cn('shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium', PHASE_COLORS[viewingOutput.phase] || 'bg-gray-800 text-gray-400')}>
                    {viewingOutput.phase}
                  </span>
                </div>
                <button
                  onClick={() => { setViewingOutput(null); setViewingOutputContent(null); }}
                  className="ml-3 shrink-0 rounded-lg p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {viewingOutputFetching ? (
                  <div className="flex items-center gap-2 py-4 text-xs text-gray-500">
                    <div className="h-3 w-3 animate-spin rounded-full border border-gray-600 border-t-gray-400" />
                    Loading output…
                  </div>
                ) : viewingOutputContent !== null ? (
                  <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-gray-300">
                    {viewingOutputContent}
                  </pre>
                ) : (
                  <MarkdownEditor value={viewingOutput.description} readOnly onChange={() => {}} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6 p-4 md:p-6">
            {/* Title */}
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLocked}
              className="w-full bg-transparent text-lg font-semibold text-gray-100 outline-none placeholder:text-gray-600 focus:ring-0 disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder="Card title"
            />

            {/* Description */}
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isLocked}
                rows={4}
                className="w-full resize-none rounded-lg border border-gray-800 bg-surface-850 p-3 text-sm text-gray-200 outline-none placeholder:text-gray-600 focus:border-primary-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Describe this card…"
              />
            </div>

            {/* Attached Images */}
            {(card.metadata.attachments?.length ?? 0) > 0 && (
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Images
                </label>
                <div className="flex flex-wrap gap-2">
                  {(card.metadata.attachments ?? []).map((att) => {
                    const url = attachmentUrls[att.id];
                    return url ? (
                      <a
                        key={att.id}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={att.filename}
                        className="block h-20 w-20 overflow-hidden rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
                      >
                        <img src={url} alt={att.filename} className="h-full w-full object-cover" />
                      </a>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Implementation Plan */}
            {(card.metadata.plan || card.planUrl || card.status === 'plan-review' || isLoadingFull) && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Implementation Plan
                </label>
                {isLoadingFull && !planLoaded ? (
                  <div className="flex items-center gap-2 py-4 text-xs text-gray-500">
                    <div className="h-3 w-3 animate-spin rounded-full border border-gray-600 border-t-gray-400" />
                    Loading…
                  </div>
                ) : isLocked || card.status !== 'plan-review' ? (
                  plan ? (
                    <MarkdownView value={plan} />
                  ) : (
                    <div className="rounded-lg border border-gray-700 bg-surface-900 p-3 text-sm italic text-gray-500">
                      No plan yet...
                    </div>
                  )
                ) : (
                  <MarkdownEditor
                    key={planLoaded ? 'plan-loaded' : 'plan-initial'}
                    value={plan}
                    onChange={setPlan}
                    placeholder="No plan yet..."
                    maxLength={50000}
                  />
                )}
              </div>
            )}

            {/* Save button */}
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={isLocked || updateMutation.isPending}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            )}

            {/* Runner Claim */}
            {isLocked && card.metadata.claim && (
              <div className="rounded-lg border border-primary-500/20 bg-primary-500/5 p-3">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Runner</label>
                <div className="space-y-1.5 text-sm text-gray-300">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">ID</span>
                    <code className="rounded bg-gray-800 px-2 py-0.5 text-xs">{card.metadata.claim.runnerId}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Phase</span>
                    <code className="rounded bg-gray-800 px-2 py-0.5 text-xs">{card.metadata.claim.phase}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Claimed</span>
                    <span className="text-xs text-gray-400">{timeAgo(card.metadata.claim.claimedAt)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Lease expires</span>
                    <span className="text-xs text-gray-400">{timeAgo(card.metadata.claim.expiresAt)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Phase action errors — surfaces silent PR/merge/deploy failures with retry */}
            {card.metadata.phaseActionErrors && Object.keys(card.metadata.phaseActionErrors).length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-red-400">Automation Errors</label>
                <div className="space-y-2">
                  {(Object.entries(card.metadata.phaseActionErrors) as Array<[PhaseActionName, { phase: string; message: string; code?: string; timestamp: string }]>).map(([action, err]) => (
                    <div key={action} className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-red-300">{ACTION_LABELS[action]}</span>
                            {err.code && (
                              <code className="rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] text-red-200">{err.code}</code>
                            )}
                            <span className="text-[10px] text-gray-500">{timeAgo(err.timestamp)}</span>
                          </div>
                          <p className="mt-1 text-sm text-red-100 break-words">{err.message}</p>
                          <p className="mt-0.5 text-[10px] text-gray-500">Phase: {err.phase}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => retryActionMutation.mutate(action)}
                          disabled={retryActionMutation.isPending}
                          className="shrink-0 rounded-md bg-red-500/20 px-3 py-1.5 text-xs font-medium text-red-100 hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                        >
                          {retryActionMutation.isPending && retryActionMutation.variables === action ? 'Retrying…' : 'Retry'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            {(card.metadata.githubBranch || card.metadata.githubPR || (card.metadata.prHistory && card.metadata.prHistory.length > 0)) && (
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">GitHub</label>
                {card.metadata.githubBranch && (
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.07-9.07l4.5-4.5a4.5 4.5 0 016.364 6.364l-1.757 1.757" />
                    </svg>
                    <code className="rounded bg-gray-800 px-2 py-0.5 text-xs">{card.metadata.githubBranch}</code>
                    {!card.metadata.githubPR && !card.metadata.phaseActionErrors?.createPR && (
                      <button
                        type="button"
                        onClick={() => retryActionMutation.mutate('createPR')}
                        disabled={retryActionMutation.isPending}
                        className="rounded-md border border-primary-500/40 bg-primary-500/10 px-2 py-0.5 text-xs text-primary-300 hover:bg-primary-500/20 disabled:opacity-50 transition-colors"
                        title="Push this branch to GitHub and open a PR"
                      >
                        {retryActionMutation.isPending && retryActionMutation.variables === 'createPR' ? 'Creating…' : 'Create PR'}
                      </button>
                    )}
                  </div>
                )}
                {card.metadata.githubPR && (
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                    </svg>
                    {card.metadata.prUrl ? (
                      <a
                        href={card.metadata.prUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded bg-gray-800 px-2 py-0.5 text-xs text-primary-400 hover:text-primary-300 hover:bg-gray-700 transition-colors"
                      >
                        {card.metadata.githubPR}
                      </a>
                    ) : (
                      <code className="rounded bg-gray-800 px-2 py-0.5 text-xs">{card.metadata.githubPR}</code>
                    )}
                    {card.metadata.prMerged && (
                      <span className="rounded-full bg-purple-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-purple-400">merged</span>
                    )}
                    {!card.metadata.prMerged && !card.metadata.phaseActionErrors?.autoMergePR && (
                      <button
                        type="button"
                        onClick={() => retryActionMutation.mutate('autoMergePR')}
                        disabled={retryActionMutation.isPending}
                        className="rounded-md border border-purple-500/40 bg-purple-500/10 px-2 py-0.5 text-xs text-purple-300 hover:bg-purple-500/20 disabled:opacity-50 transition-colors"
                        title="Squash-merge this PR"
                      >
                        {retryActionMutation.isPending && retryActionMutation.variables === 'autoMergePR' ? 'Merging…' : 'Merge PR'}
                      </button>
                    )}
                    {card.metadata.prMerged && !card.metadata.phaseActionErrors?.trackDeployment && card.metadata.deploymentStatus !== 'deployed' && (
                      <button
                        type="button"
                        onClick={() => retryActionMutation.mutate('trackDeployment')}
                        disabled={retryActionMutation.isPending}
                        className="rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-xs text-orange-300 hover:bg-orange-500/20 disabled:opacity-50 transition-colors"
                        title="Refresh deployment status from GitHub Actions"
                      >
                        {retryActionMutation.isPending && retryActionMutation.variables === 'trackDeployment' ? 'Checking…' : 'Check Deploy'}
                      </button>
                    )}
                  </div>
                )}
                {card.metadata.prHistory && card.metadata.prHistory.length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-gray-800 pt-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      Previous iterations ({card.metadata.prHistory.length})
                    </div>
                    {card.metadata.prHistory.map((entry, idx) => (
                      <div key={`${entry.url ?? entry.number ?? entry.archivedAt}-${idx}`} className="flex items-center gap-2 text-xs text-gray-400">
                        <span className="text-gray-600">{card.metadata.prHistory!.length - idx}.</span>
                        {entry.url ? (
                          <a
                            href={entry.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded bg-gray-800/60 px-2 py-0.5 text-primary-400/80 hover:text-primary-300 hover:bg-gray-700 transition-colors"
                          >
                            {entry.number ? `#${entry.number}` : entry.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}
                          </a>
                        ) : entry.number ? (
                          <code className="rounded bg-gray-800/60 px-2 py-0.5">#{entry.number}</code>
                        ) : (
                          <span className="text-gray-500">PR</span>
                        )}
                        {entry.branch && (
                          <code className="rounded bg-gray-900/80 px-1.5 py-0.5 text-[10px] text-gray-500">{entry.branch}</code>
                        )}
                        {entry.merged && (
                          <span className="rounded-full bg-purple-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-purple-400/80">merged</span>
                        )}
                        <span className="ml-auto text-gray-600">{new Date(entry.archivedAt).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tags (auto-generated, read-only) */}
            {tags.length > 0 && (
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Tags <span className="normal-case font-normal text-gray-600">(auto-generated)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-gray-800 px-2.5 py-1 text-xs text-gray-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Blocked By (prerequisite cards) */}
            {allCards && (() => {
              const otherCards = allCards.filter((c) => c.id !== card.id);
              const doneColumnIds = new Set(
                (board?.columns ?? []).filter((col) => col.status === 'done').map((col) => col.id)
              );
              const selectedCards = blockedBy
                .map((id) => otherCards.find((c) => c.id === id))
                .filter((c): c is Card => !!c);
              const unresolvedCount = selectedCards.filter((c) => !doneColumnIds.has(c.columnId)).length;
              return (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Blocked By{' '}
                      <span className="normal-case font-normal text-gray-600">
                        (prerequisites)
                      </span>
                    </label>
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={() => setShowBlockedByPicker((s) => !s)}
                        className="text-[10px] font-medium text-primary-400 hover:text-primary-300"
                      >
                        {showBlockedByPicker ? 'Done' : selectedCards.length === 0 ? '+ Add' : 'Edit'}
                      </button>
                    )}
                  </div>
                  {selectedCards.length > 0 ? (
                    <div className="space-y-1">
                      {selectedCards.map((prereq) => {
                        const isDone = doneColumnIds.has(prereq.columnId);
                        return (
                          <div
                            key={prereq.id}
                            className="flex items-center gap-2 rounded-lg bg-surface-850 px-3 py-2 text-sm"
                          >
                            <span
                              className={cn(
                                'h-2 w-2 shrink-0 rounded-full',
                                isDone ? 'bg-green-500' : 'bg-amber-500'
                              )}
                              title={isDone ? 'Prerequisite met' : 'Prerequisite not met'}
                            />
                            <span className="min-w-0 flex-1 truncate text-gray-300">
                              {prereq.title || prereq.description.slice(0, 60) || 'Untitled'}
                            </span>
                            {!isLocked && (
                              <button
                                type="button"
                                onClick={() => setBlockedBy((prev) => prev.filter((id) => id !== prereq.id))}
                                className="shrink-0 rounded p-0.5 text-gray-500 hover:bg-gray-700 hover:text-red-400"
                                title="Remove prerequisite"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      })}
                      {unresolvedCount > 0 && (
                        <p className="mt-1 text-[11px] text-amber-400">
                          🔒 This card cannot advance while {unresolvedCount} prerequisite
                          {unresolvedCount === 1 ? '' : 's'} remain{unresolvedCount === 1 ? 's' : ''} unresolved.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No prerequisites set.</p>
                  )}
                  {showBlockedByPicker && !isLocked && (
                    <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-gray-800 bg-surface-900 p-2">
                      {otherCards.length === 0 ? (
                        <p className="p-2 text-xs text-gray-500">No other cards on this board.</p>
                      ) : (
                        otherCards.map((other) => {
                          const checked = blockedBy.includes(other.id);
                          return (
                            <label
                              key={other.id}
                              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-300 hover:bg-surface-800"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setBlockedBy((prev) => (prev.includes(other.id) ? prev : [...prev, other.id]));
                                  } else {
                                    setBlockedBy((prev) => prev.filter((id) => id !== other.id));
                                  }
                                }}
                                className="h-3.5 w-3.5 accent-primary-500"
                              />
                              <span className="min-w-0 flex-1 truncate">
                                {other.title || other.description.slice(0, 60) || 'Untitled'}
                              </span>
                              <span className="shrink-0 text-[10px] text-gray-500">
                                {doneColumnIds.has(other.columnId) ? '✓ done' : other.status}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Agents */}
            {card.assignedAgents.length > 0 && (
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Assigned Agents
                </label>
                <div className="space-y-1">
                  {card.assignedAgents.map((agent) => (
                    <div key={agent} className="flex items-center gap-2 rounded-lg bg-surface-850 px-3 py-2 text-sm text-gray-300">
                      <div className="h-5 w-5 rounded-full bg-primary-600" />
                      {agent}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Displacement Alert */}
            {card.metadata.needsReeval && (
              <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <svg className="h-4 w-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                  <span className="text-sm font-semibold text-orange-400">Card Displaced</span>
                </div>
                <p className="text-xs text-gray-300">{card.metadata.reevalReason}</p>
                <button
                  onClick={() => reevalMutation.mutate()}
                  disabled={reevalMutation.isPending}
                  className="mt-3 rounded-lg bg-orange-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-orange-500 disabled:opacity-50"
                >
                  {reevalMutation.isPending ? 'Acknowledging...' : 'Acknowledged'}
                </button>
              </div>
            )}

            {/* Questions */}
            {(card.metadata.questions?.length ?? 0) > 0 && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Questions
                  </label>
                  {card.metadata.questions!.some(q => q.status === 'open') && (
                    <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-400">
                      {card.metadata.questions!.filter(q => q.status === 'open').length} open
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  {card.metadata.questions!.map((q) => (
                    <div
                      key={q.id}
                      className={cn(
                        'rounded-lg border p-3',
                        q.status === 'open'
                          ? 'border-amber-500/30 bg-amber-500/5'
                          : 'border-gray-800 bg-surface-850'
                      )}
                    >
                      <p className="text-sm font-medium text-gray-200">{q.question}</p>
                      {q.context && (
                        <p className="mt-1 text-xs text-gray-500">{q.context}</p>
                      )}
                      {q.status === 'answered' ? (
                        <div className="mt-2 rounded-md bg-gray-800/50 px-3 py-2">
                          <p className="text-xs text-gray-400">{q.answer}</p>
                          <p className="mt-1 text-[10px] text-gray-600">{timeAgo(q.answeredAt!)}</p>
                        </div>
                      ) : (
                        <div className="mt-2 flex gap-2">
                          <input
                            value={answerInputs[q.id] || ''}
                            onChange={(e) => setAnswerInputs((prev) => ({ ...prev, [q.id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAnswer(q.id);
                              }
                            }}
                            className="flex-1 rounded-lg border border-amber-500/30 bg-surface-850 px-3 py-1.5 text-xs text-gray-200 outline-none placeholder:text-gray-600 focus:border-amber-500/50"
                            placeholder="Type your answer..."
                          />
                          <button
                            onClick={() => handleAnswer(q.id)}
                            disabled={!answerInputs[q.id]?.trim() || answerMutation.isPending}
                            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-40"
                          >
                            Answer
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send Prompt */}
            {!isLocked && (
              <div>
                {!promptExpanded ? (
                  <button
                    onClick={() => setPromptExpanded(true)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-700 py-2.5 text-xs font-medium text-gray-400 transition-colors hover:border-primary-500/50 hover:text-primary-400"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                    </svg>
                    Send Prompt
                  </button>
                ) : (
                  <div className="rounded-lg border border-primary-500/30 bg-primary-500/5 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Send Prompt
                      </label>
                      <button
                        onClick={() => { setPromptExpanded(false); setPromptText(''); }}
                        className="text-xs text-gray-500 hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                    <textarea
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      placeholder="Tell the agent what to do next..."
                      rows={3}
                      className="mb-2 w-full resize-none rounded-lg border border-gray-700 bg-surface-850 px-3 py-2 text-sm text-gray-200 outline-none placeholder:text-gray-600 focus:border-primary-500/50"
                    />
                    <div className="flex items-center gap-2">
                      {templateAgents.length > 0 && (
                        <select
                          value={promptAgentRoleId}
                          onChange={(e) => setPromptAgentRoleId(e.target.value)}
                          className="rounded-lg border border-gray-700 bg-surface-850 px-2 py-1.5 text-xs text-gray-300 outline-none focus:border-primary-500/50"
                        >
                          {templateAgents.map((agent: any) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.name} ({agent.type})
                            </option>
                          ))}
                        </select>
                      )}
                      <div className="flex-1" />
                      <button
                        onClick={() => {
                          if (!promptText.trim() || !promptAgentRoleId) return;
                          promptMutation.mutate({ prompt: promptText.trim(), agentRoleId: promptAgentRoleId });
                        }}
                        disabled={!promptText.trim() || !promptAgentRoleId || promptMutation.isPending}
                        className="rounded-lg bg-primary-600 px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-500 disabled:opacity-40"
                      >
                        {promptMutation.isPending ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Activity Feed */}
            <div>
              <label className="mb-3 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Activity
              </label>
              {activitiesLoading ? (
                <div className="flex items-center gap-2 py-2 text-xs text-gray-500">
                  <div className="h-3 w-3 animate-spin rounded-full border border-gray-600 border-t-gray-400" />
                  Loading…
                </div>
              ) : activities.length === 0 ? (
                <p className="text-sm text-gray-600">No activity yet</p>
              ) : (
                <div className="space-y-0">
                  {activities.map((activity, i) => {
                    const dotColor = ACTIVITY_DOT_COLORS[activity.type] || 'bg-gray-500';
                    const actor = (activity.metadata as any)?.actor || activity.agentId;
                    const isAgent = !actor.includes('@') && !actor.includes('-') || ['triager', 'architect', 'reviewer', 'builder', 'tester', 'deployer', 'monitor', 'ci-bot', 'github-bot'].includes(actor);
                    const fromCol = (activity.metadata as any)?.fromColumnName;
                    const toCol = (activity.metadata as any)?.toColumnName;
                    const isLast = i === activities.length - 1;

                    return (
                      <div key={activity.id} className="flex gap-3">
                        {/* Timeline spine */}
                        <div className="flex flex-col items-center">
                          <div className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', dotColor)} />
                          {!isLast && <div className="w-px flex-1 bg-gray-800" />}
                        </div>

                        {/* Content */}
                        <div className="flex-1 pb-4">
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm font-medium text-gray-200">{activity.title}</span>
                            <span className={cn('rounded-md px-1.5 py-0.5 text-[10px] font-medium', PHASE_COLORS[activity.phase] || 'bg-gray-800 text-gray-400')}>
                              {activity.phase}
                            </span>
                          </div>

                          {/* Move arrow */}
                          {fromCol && toCol ? (
                            <div className="mt-1 flex items-center gap-1.5 text-xs">
                              <span className="rounded bg-gray-800 px-1.5 py-0.5 text-gray-400">{fromCol}</span>
                              <svg className="h-3 w-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                              </svg>
                              <span className="rounded bg-gray-800 px-1.5 py-0.5 text-gray-300">{toCol}</span>
                            </div>
                          ) : activity.description ? (
                            <div className="mt-0.5">
                              <p className="line-clamp-2 text-xs text-gray-500">{activity.description}</p>
                              {(activity.contentUrl || activity.description.includes('\n') || activity.description.length > 120) && (
                                <button
                                  onClick={() => handleViewOutput(activity)}
                                  className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-primary-600/20 px-2 py-0.5 text-[10px] font-medium text-primary-400 transition-colors hover:bg-primary-600/30 hover:text-primary-300 active:bg-primary-600/40"
                                >
                                  <svg className="h-2.5 w-2.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                  </svg>
                                  View output
                                </button>
                              )}
                            </div>
                          ) : null}

                          <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-600">
                            {isAgent ? (
                              <span className="rounded bg-purple-500/10 px-1.5 py-0.5 text-purple-400">{actor}</span>
                            ) : (
                              <span className="text-gray-500">{actor}</span>
                            )}
                            <span>{timeAgo(activity.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
